import { Brain, Search, Bell, Settings, User, LogOut, Building2 } from "lucide-react"
import React from "react"
import { useNavigate } from "react-router-dom"
import { GlobalSearch } from "./global-search"
import { MobileNavigation } from "./mobile-navigation"
import { LanguageSwitcher } from "./language-switcher"
import { TranslatedText } from "./translated-text"
import { useAuth } from "../contexts/auth-context"
import { useSidebar } from "../contexts/sidebar-context"
import { Button } from "./ui/button"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { apiService } from "../lib/api"
import { Alert, AlertDescription, AlertTitle } from "./ui/alert"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu"

export function Header() {
  const { user, logout, isAuthenticated, isLoading } = useAuth()
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => setMounted(true), [])
  const qc = useQueryClient()
  
  React.useEffect(() => {
  }, [isAuthenticated, user, isLoading])
  
  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const result = await apiService.getCompanies({ page: 1, pageSize: 100 }) as any;
      // Handle different response formats
      if (Array.isArray(result)) {
        return result;
      }
      if (result?.data && Array.isArray(result.data)) {
        return result.data;
      }
      if (result?.items && Array.isArray(result.items)) {
        return result.items;
      }
      return [];
    },
    enabled: isAuthenticated
  })
  
  const companiesList = React.useMemo(() => {
    if (!companies) return [];
    
    let allCompanies = [];
    if (Array.isArray(companies)) {
      allCompanies = companies;
    } else if (companies.data && Array.isArray(companies.data)) {
      allCompanies = companies.data;
    } else if (companies.items && Array.isArray(companies.items)) {
      allCompanies = companies.items;
    } else {
      return [];
    }
    
    return allCompanies;
  }, [companies])
  
  // Get active company from localStorage
  const [activeCompany, setActiveCompany] = React.useState<string | undefined>(() => {
    if (typeof window === 'undefined') return undefined;
    return localStorage.getItem('company_id') || undefined;
  })
  
  const [activeCompanyName, setActiveCompanyName] = React.useState<string | undefined>(() => {
    if (typeof window === 'undefined') return undefined;
    return localStorage.getItem('company_name') || undefined;
  })
  
  // Listen for company changes in localStorage
  React.useEffect(() => {
    if (!mounted) return
    
    const updateCompany = () => {
      try {
        const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId') || localStorage.getItem('company')
        const companyName = localStorage.getItem('company_name')
        
        if (companyId && companyId !== activeCompany) {
          setActiveCompany(companyId)
        }
        if (companyName && companyName !== activeCompanyName) {
          setActiveCompanyName(companyName)
        }
      } catch (error) {
        console.error('❌ Error reading company from localStorage:', error)
      }
    }
    
    // Initial update
    updateCompany()
    
    // Listen for storage changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'company_id' || e.key === 'companyId' || e.key === 'company' || e.key === 'company_name') {
        updateCompany()
      }
    }
    
    // Listen for custom company change event
    const handleCompanyChange = (e: CustomEvent) => {
      updateCompany()
    }
    
    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('companyChanged', handleCompanyChange as EventListener)
    
    // Periodic check to sync with localStorage
    const interval = setInterval(updateCompany, 1000)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('companyChanged', handleCompanyChange as EventListener)
      clearInterval(interval)
    }
  }, [mounted, activeCompany, activeCompanyName])

  const onCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCompanyId = e.target.value
    const selectedCompany = companiesList.find((c: any) => c.id === newCompanyId)
    
    if (selectedCompany) {
      try {
        localStorage.setItem('company_id', newCompanyId)
        localStorage.setItem('companyId', newCompanyId)
        localStorage.setItem('company', newCompanyId)
        if (selectedCompany.name) {
          localStorage.setItem('company_name', selectedCompany.name)
        }
        
        // Dispatch custom event for other components
        window.dispatchEvent(new CustomEvent('companyChanged', { 
          detail: { companyId: newCompanyId, companyName: selectedCompany.name } 
        }))
        
        // Refresh the page to ensure all components reload with the new company
        window.location.reload()
      } catch (error) {
        console.error('❌ Error saving company to localStorage:', error)
      }
    }
  }

  if (!mounted) {
    return (
      <header 
        className={`fixed top-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border transition-all duration-300 ${
          isCollapsed ? 'left-16' : 'left-64'
        }`} 
        suppressHydrationWarning
      >
        <div className="h-14 lg:h-16" />
      </header>
    )
  }

  // Don't render if auth is still loading
  if (isLoading) {
    return (
      <header className="h-16 bg-white border-b border-gray-200 px-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse"></div>
        </div>
      </header>
    )
  }

  return (
    <header 
      className={`fixed top-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border transition-all duration-300 ${
        isCollapsed ? 'left-16' : 'left-64'
      }`}
    >
      <div className="flex items-center justify-between px-4 py-3 lg:px-6 lg:py-4">
        {/* Left side - Logo and Brand */}
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Brain className="w-5 h-5 text-primary-foreground" />
          </div>
          <div className="hidden sm:block">
            <h1 className="text-lg font-bold text-foreground">UrutiIQ</h1>
            <p className="text-xs text-muted-foreground">AI Accounting</p>
          </div>
        </div>

        {/* Center - Search and Client Selector */}
        <div className="hidden md:flex items-center gap-4 flex-1 max-w-2xl mx-8">
          <div className="flex-1">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <select 
              value={activeCompany || ''} 
              onChange={onCompanyChange} 
              className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary min-w-[200px] cursor-pointer"
              disabled={!isAuthenticated || companiesList.length === 0}
              title={activeCompanyName || "Select a company"}
            >
              {companiesList.length > 0 ? (
                companiesList.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name || c.id}</option>
                ))
              ) : (
                <option value=""><TranslatedText text="No companies available" /></option>
              )}
            </select>
          </div>
        </div>

        {/* Right side - Actions and User */}
        <div className="flex items-center gap-2">
          {/* Language Switcher */}
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>
          
          <button className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Search className="w-4 h-4 md:hidden" />
          </button>
          <button className="p-2 hover:bg-muted rounded-lg transition-colors relative">
            <Bell className="w-4 h-4" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
          <button className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Settings className="w-4 h-4" />
          </button>
          
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src="" alt={user.firstName} />
                    <AvatarFallback>
                      {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user.email}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user.companyName}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  <span><TranslatedText text="Profile" /></span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  <span><TranslatedText text="Settings" /></span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout()}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span><TranslatedText text="Log out" /></span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="sm">
              <User className="w-4 h-4" />
            </Button>
          )}
          
          <div className="md:hidden">
            <MobileNavigation />
          </div>
        </div>
      </div>

      {/* Company prompt when none exists */}
      {/* {mounted && companiesList.length === 0 && (
        <div className="px-4 lg:px-6 pb-3">
          <Alert>
            <AlertTitle>No company found</AlertTitle>
            <AlertDescription>
              Create a company to start recording transactions. Go to Settings to add your first company.
            </AlertDescription>
          </Alert>
        </div>
      )} */}
    </header>
  )
}
